/**
 * Canonical set of stable, machine-readable failure codes for a failed video /
 * batch transcription. Single source of truth shared by the API (which derives
 * the code from the sanitized `errorMessage`) and the web app (which maps it to
 * a localized message).
 *
 * Adding a code here forces both apps to acknowledge it at the type level,
 * which is the point: it stops the code list from silently drifting across the
 * API serializer, the web renderer, the OpenAPI spec, and the i18n files. When
 * you add a code, also add it to: `MESSAGE_TO_CODE` in apps/api
 * error_sanitizer.ts, the `errorCode` enum in apps/api/docs/openapi.yaml, and a
 * `transcriptionError.<code>` key in BOTH web locale files (a core test guards
 * the i18n side).
 */
export const TRANSCRIPTION_FAILURE_CODES = [
  'timed_out',
  'download_failed',
  'source_access_denied',
  'source_not_found',
  'audio_processing_failed',
  'upload_failed',
  'transcription_failed',
  'visual_transcription_failed',
  'enhancement_failed',
  'dispatch_failed',
  'cancelled_by_user',
  'insufficient_credits',
  'file_too_long_for_plan',
  'failed',
] as const

export type TranscriptionFailureCode = (typeof TRANSCRIPTION_FAILURE_CODES)[number]

const CODE_SET: ReadonlySet<string> = new Set(TRANSCRIPTION_FAILURE_CODES)

/** Type guard: is `value` one of the canonical transcription failure codes? */
export function isTranscriptionFailureCode(
  value: string | null | undefined
): value is TranscriptionFailureCode {
  return value != null && CODE_SET.has(value)
}
